import React, { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Thumbnail from '../Thumbnail';
import ThumbnailNoImage from '../ThumbnailNoImage';
import ThumbnailTracked from '../ThumbnailTracked';
import * as Types from '../../types';
import Icon from '../Icon/Icon';
import { useSegmentationState } from '../../contextProviders';
import {
  extractPlanningIdFromDescription,
  isValidObjectId,
} from '../../hooks/usePlanningCustomName';

let hasCompletedInitialConfigurationLoading = false;

const ThumbnailList = ({
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
}) => {
  // Filter and memoize filtered thumbnails
  const filteredThumbnails = useMemo(
    () =>
      thumbnails.filter(
        t =>
          t.description &&
          !t.description.includes('Segmentation of morphologies') &&
          !t.modality.includes('CT')
      ),
    [thumbnails]
  );

  // Extract descriptions for precaching
  const descriptions = useMemo(
    () => filteredThumbnails.map(t => t.description).filter(Boolean),
    [filteredThumbnails]
  );

  const hasObjectIdsToResolve = useMemo(
    () =>
      descriptions.some(description => {
        const planningId = extractPlanningIdFromDescription(description);
        return planningId ? isValidObjectId(planningId) : false;
      }),
    [descriptions]
  );

  const segmentationState = useSegmentationState() as any;
  const thumbnailCustomNamesByPlanningId = segmentationState.thumbnailCustomNamesByPlanningId;
  const thumbnailCustomNamesLoading = segmentationState.thumbnailCustomNamesLoading;
  const precacheThumbnailCustomNames = segmentationState.precacheThumbnailCustomNames;

  useEffect(() => {
    precacheThumbnailCustomNames(descriptions);
  }, [descriptions, precacheThumbnailCustomNames]);

  const initialLoadingStartedRef = useRef(false);

  useEffect(() => {
    if (hasCompletedInitialConfigurationLoading || !hasObjectIdsToResolve) {
      return;
    }

    if (thumbnailCustomNamesLoading) {
      initialLoadingStartedRef.current = true;
      return;
    }

    if (initialLoadingStartedRef.current && !thumbnailCustomNamesLoading) {
      hasCompletedInitialConfigurationLoading = true;
    }
  }, [hasObjectIdsToResolve, thumbnailCustomNamesLoading]);

  const shouldShowLoadingConfigurations =
    !hasCompletedInitialConfigurationLoading &&
    filteredThumbnails.length > 0 &&
    hasObjectIdsToResolve &&
    thumbnailCustomNamesLoading;
  return (
    <div
      id="ohif-thumbnail-list"
      className="bg-gray-100 py-5"
    >
      <div
        role="region"
        aria-label="Configuration instructions"
        className="mx-2 mb-4 flex items-center gap-4 rounded border border-[#4299e1] bg-white px-4 py-3"
      >
        <Icon
          name="info"
          className="h-5 w-5 flex-shrink-0 text-[#4299e1]"
          aria-hidden="true"
        />
        <div className="flex flex-col gap-2">
          <p className="text-left text-sm text-gray-700">
            Here you can view all virtually implanted devices
          </p>
          <p className="text-left text-base font-bold text-gray-900">
            <span className="text-[#4299e1]">Double-click</span> a configuration to overlay it on
            the CT.
          </p>
        </div>
      </div>
      <div className="flex max-h-[500px] flex-col items-center gap-4 overflow-y-auto overflow-x-hidden px-2">
        {filteredThumbnails.length === 0 ? (
          <div className="mt-4 text-center text-base font-bold text-black">
            No saved configurations
          </div>
        ) : (
          <>
            {shouldShowLoadingConfigurations && (
              <div className="mt-4 text-center text-base font-bold text-black">
                Loading configurations...
              </div>
            )}

            <div
              className={
                shouldShowLoadingConfigurations ? 'w-full opacity-0' : 'w-full opacity-100'
              }
            >
              {filteredThumbnails.map(
                ({
                  displaySetInstanceUID,
                  description,
                  dragData,
                  seriesNumber,
                  numInstances,
                  modality,
                  componentType,
                  seriesDate,
                  countIcon,
                  isTracked,
                  canReject,
                  onReject,
                  imageSrc,
                  messages,
                  imageAltText,
                  isHydratedForDerivedDisplaySet,
                }) => {
                  const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
                  const planningId = extractPlanningIdFromDescription(description);
                  const customName = planningId
                    ? thumbnailCustomNamesByPlanningId[planningId]
                    : null;

                  switch (componentType) {
                    case 'thumbnailNoImage':
                      return (
                        <div key={displaySetInstanceUID}>
                          <ThumbnailNoImage
                            isActive={isActive}
                            key={displaySetInstanceUID}
                            displaySetInstanceUID={displaySetInstanceUID}
                            dragData={dragData}
                            modality={modality}
                            modalityTooltip={_getModalityTooltip(modality)}
                            messages={messages}
                            seriesDate={seriesDate}
                            description={description}
                            customName={customName}
                            canReject={canReject}
                            onReject={onReject}
                            onClick={() => onThumbnailClick(displaySetInstanceUID)}
                            onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
                            isHydratedForDerivedDisplaySet={isHydratedForDerivedDisplaySet}
                          />
                        </div>
                      );
                    default:
                      return <></>;
                  }
                }
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ThumbnailList.propTypes = {
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      displaySetInstanceUID: PropTypes.string.isRequired,
      imageSrc: PropTypes.string,
      imageAltText: PropTypes.string,
      seriesDate: PropTypes.string,
      seriesNumber: Types.StringNumber,
      numInstances: PropTypes.number,
      description: PropTypes.string,
      componentType: Types.ThumbnailType.isRequired,
      isTracked: PropTypes.bool,
      /**
       * Data the thumbnail should expose to a receiving drop target. Use a matching
       * `dragData.type` to identify which targets can receive this draggable item.
       * If this is not set, drag-n-drop will be disabled for this thumbnail.
       *
       * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
       */
      dragData: PropTypes.shape({
        /** Must match the "type" a dropTarget expects */
        type: PropTypes.string.isRequired,
      }),
    })
  ),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  onThumbnailClick: PropTypes.func.isRequired,
  onThumbnailDoubleClick: PropTypes.func.isRequired,
  onClickUntrack: PropTypes.func.isRequired,
};

// TODO: Support "Viewport Identificator"?
function _getModalityTooltip(modality) {
  if (_modalityTooltips.hasOwnProperty(modality)) {
    return _modalityTooltips[modality];
  }

  return 'Unknown';
}

const _modalityTooltips = {
  SR: 'Structured Report',
  SEG: 'Segmentation',
  RTSTRUCT: 'RT Structure Set',
};

export default ThumbnailList;
